import React, { createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { COPY_TARGETS_STALE_ERROR, IComment, IVideoFeedbackProps, REVIEW_LOCKED_ERROR } from "./constants";
import { BrandProject } from "../constants";
import { BRAND_PROJECT_STATUSES } from "../../helper";
import axiosInstant from "../../../../api";
import { apiErrorCode } from "../../../../utils/apiError";

interface IVideoFeedbackContext extends IVideoFeedbackProps {
    isDirty?: boolean;
    comments: Array<IComment>;
    onCreateComment: (comment: IComment) => Promise<void>;
    onUpdateComment: (comment: IComment) => Promise<void>;
    onDeleteComment: (commentId?: string) => void;
    getComments: (s3FileId: any) => void;
    onRequestEdit?: () => void;
    onApprove?: () => Promise<any>;
    isCommentLoading: boolean;
    setCommentLoading: (loading: boolean) => void;
    guestName?: string;
    onGuestNameChange?: (name: string) => void;
    isPublicPage?: boolean;
    /**
     * True once feedback has been submitted and the project is back in
     * Editing: the video stays playable but comments can't be added, edited
     * or deleted and feedback can't be re-submitted. Derived from the project
     * status so every entry point (public share, brand portal, admin videos)
     * locks the same way; the API enforces the same rule (409
     * FeedbackAlreadySubmitted) for non-internal users.
     */
    isReviewLocked: boolean;
    /**
     * Copies a comment (message and timestamps) onto other videos of the
     * project as new comments by the comment's original author; resolves with how many
     * were created. Undefined on public pages and token-auth providers
     * (createCommentFn), which have no bearer for the copy endpoint.
     */
    onCopyComment?: (commentId: string, fileIds: number[]) => Promise<number>;
    /** The comment the "Apply to other videos" picker is open for. */
    commentToApply: IComment | null;
    setCommentToApply: (comment: IComment | null) => void;
    /**
     * Closes the review this provider is mounted in. Set by modals that hold
     * their own open state, so "Applied to" can leave before it navigates.
     */
    onCloseReview?: () => void;
    readOnly: boolean;
}

const VideoFeedbackContext = createContext<IVideoFeedbackContext>({
    project: {} as BrandProject,
    video: {},
    comments: [],
    isDirty: false,
    onCreateComment: async () => {},
    onUpdateComment: async () => {},
    onDeleteComment: () => {},
    getComments: () => {},
    onRequestEdit: () => {},
    onApprove: async () => {},
    isCommentLoading: false,
    setCommentLoading: () => {},
    isReviewLocked: false,
    commentToApply: null,
    setCommentToApply: () => {},
    readOnly: false
});

interface IVideoFeedbackProvider extends IVideoFeedbackProps {
    comments: Array<IComment>;
    children: ReactNode;
    createCommentFn?: (comment: IComment) => Promise<IComment | void>;
    getCommentsFn?: (s3FileId: any) => Promise<IComment[] | void>;
    onApproveFn?: (fileId: number) => Promise<any>;
    onRequestEditFn?: (fileId: number) => Promise<any>;
    guestName?: string;
    onGuestNameChange?: (name: string) => void;
    isPublicPage?: boolean;
    onCloseReview?: () => void;
    readOnly?: boolean;
}

export const VideoFeedbackProvider: FC<IVideoFeedbackProvider> = ({
    comments = [],
    children,
    createCommentFn,
    getCommentsFn,
    onApproveFn,
    onRequestEditFn,
    guestName,
    onGuestNameChange,
    isPublicPage,
    onCloseReview,
    readOnly = false,
    project,
    video,
    revisions,
    disableCommentMessage,
    showFeedbackCriteria,
    downloadFileTypes,
    onEditAsComplete,
    onFeedbackSubmit,
    onApproved
}) => {
    const [allComments, setAllComments] = useState<Array<IComment>>(comments);
    const [isDirty, setIsDirty] = useState(false);
    const [isCommentLoading, setIsCommentLoading] = useState(false);
    const [commentToApply, setCommentToApply] = useState<IComment | null>(null);
    const isMounted = useRef(true);
    useEffect(
        () => () => {
            isMounted.current = false;
        },
        []
    );

    const projectId = project?.id;
    const videoId = video?.id;
    const copyProjectId = projectId || video?.projectId;
    const isReviewLocked =
        readOnly || project?.status === BRAND_PROJECT_STATUSES.EDITING;

    const getRmnBrandUrl = useCallback((): string => {
        if (!projectId || !videoId) {
            return "";
        }

        let brandId: number | undefined = project?.brand_id;

        if (!brandId) {
            brandId = project?.brands?.find((brand) => brand.rmn === true)?.id;
        }

        if (!brandId && project?.brands?.length > 0) {
            brandId = project.brands[0].id;
        }

        if (!brandId && project?.brand?.id) {
            brandId = project.brand.id;
        }

        if (!brandId) {
            return "";
        }

        return `brands/${brandId}/projects/${projectId}/file/${videoId}`;
    }, [project, projectId, videoId]);

    const getComments = useCallback(
        async (s3FileId?: any) => {
            if (getCommentsFn) {
                const overrideComments = await getCommentsFn(s3FileId);
                if (!isMounted.current) return;
                if (overrideComments) setAllComments(overrideComments);
                setIsCommentLoading(false);
                return;
            }

            if (!projectId || !videoId) return;

            try {
                const { data } = await axiosInstant.get(`/brand-projects/${projectId}/${videoId}/messages`);
                if (!isMounted.current) return;
                setAllComments((data || []).filter((c: IComment) => c.s3_file_id === videoId));
                setIsCommentLoading(false);
            } catch (error) {
                if (!isMounted.current) return;
                setAllComments([]);
                setIsCommentLoading(false);
            }
        },
        [getCommentsFn, projectId, videoId]
    );

    const onCreateComment = useCallback(
        async (comment: IComment) => {
            if (!videoId || !projectId) return;

            setIsCommentLoading(true);
            try {
                // The backend emits its projectMessage socket event before the POST
                // resolves; the echo-triggered refetch can land first and already
                // contain the new row, so never append an id that is present.
                const appendUnlessPresent = (created: IComment) =>
                    setAllComments((prev) =>
                        created.id != null && prev.some((c) => String(c.id) === String(created.id))
                            ? prev
                            : [...prev, created]
                    );

                if (createCommentFn) {
                    const created = await createCommentFn(comment);
                    if (!isMounted.current) return;
                    if (created) appendUnlessPresent(created);
                } else {
                    const payload = { ...comment, s3_file_id: videoId };
                    let data: any;
                    try {
                        ({ data } = await axiosInstant.post(
                            `/brand-projects/${projectId}/file/${videoId}/message`,
                            payload
                        ));
                    } catch (err) {
                        // CreateCommentPanel toasts the thrown message and keeps
                        // the draft; a bare axios message ("status code 409") is
                        // not copy.
                        if (apiErrorCode(err) === "FeedbackAlreadySubmitted") {
                            throw new Error(REVIEW_LOCKED_ERROR);
                        }
                        throw new Error("Failed to add comment. Please try again.");
                    }
                    if (!isMounted.current) return;
                    // The POST returns the created row without the user association,
                    // so keep the local user for display until the next full fetch.
                    // created_at fallback: the grouped list hides comments without one.
                    appendUnlessPresent({
                        ...comment,
                        ...data,
                        created_at: data?.created_at || new Date().toISOString(),
                        user: comment.user
                    });
                }
            } finally {
                if (isMounted.current) setIsCommentLoading(false);
            }
        },
        [createCommentFn, projectId, videoId]
    );

    const onUpdateComment = useCallback(
        async (comment: IComment) => {
            const { id } = comment;
            if (!videoId || !projectId || !id) return;

            try {
                const payload = { ...comment, s3_file_id: videoId };
                const { data } = await axiosInstant.patch(`/brand-projects/${projectId}/messages/${id}`, payload);
                if (!isMounted.current) return;
                // Like the POST, the PATCH response has no user association.
                setAllComments((prev) =>
                    prev.map((c) => (String(c.id) === String(id) ? { ...c, ...comment, ...data, user: c.user } : c))
                );
            } catch (error) {
                if (isMounted.current) toast.error("Failed to update comment");
            }
        },
        [projectId, videoId]
    );

    const onDeleteComment = useCallback(
        async (commentId?: string) => {
            if (!commentId || !projectId) return;

            try {
                await axiosInstant.delete(`/brand-projects/${projectId}/messages/${commentId}`);
                if (!isMounted.current) return;
                // The API soft-deletes; internal users still see the comment flagged as deleted.
                setAllComments((prev) =>
                    prev.map((c) => (String(c.id) === String(commentId) ? { ...c, deleted: true } : c))
                );
            } catch (error) {
                if (isMounted.current) toast.error("Failed to delete comment");
            }
        },
        [projectId]
    );

    const copyComment = useCallback(
        async (commentId: string, fileIds: number[]) => {
            if (!copyProjectId) throw new Error("Couldn't apply the comment. Please try again.");

            // Copies land on other videos' threads, never this one (the picker
            // keeps this video's lineage out); only this thread's "Applied to"
            // chips change, so it is refetched below.
            let data: any;
            try {
                ({ data } = await axiosInstant.post(`/brand-projects/${copyProjectId}/messages/${commentId}/copy`, {
                    fileIds
                }));
            } catch (err) {
                // The picker toasts the thrown message and stays open.
                const code = apiErrorCode(err);
                if (code === "FeedbackAlreadySubmitted") throw new Error(REVIEW_LOCKED_ERROR);
                if (code === "InvalidTargetFiles") {
                    // Carries the rejected ids so the picker can mark them.
                    const stale: Error & { invalidFileIds?: number[] } = new Error(COPY_TARGETS_STALE_ERROR);
                    stale.invalidFileIds = (err as any)?.response?.data?.invalidFileIds || [];
                    throw stale;
                }
                throw new Error("Couldn't apply the comment. Please try again.");
            }
            if (isMounted.current) getComments(videoId);
            return Number(data?.count) || 0;
        },
        [copyProjectId, getComments, videoId]
    );
    const onCopyComment = createCommentFn || isPublicPage ? undefined : copyComment;

    const onRequestEdit = useCallback(async () => {
        if (onRequestEditFn) {
            if (!videoId) return undefined;
            return onRequestEditFn(videoId);
        }

        const baseUrl = getRmnBrandUrl();
        if (!baseUrl) return undefined;

        return axiosInstant.patch(`${baseUrl}/request-edit`, {});
    }, [onRequestEditFn, videoId, getRmnBrandUrl]);

    const onApprove = useCallback(async () => {
        if (onApproveFn) {
            if (!videoId) throw new Error("Cannot approve video: missing file information");
            return onApproveFn(videoId);
        }

        const baseUrl = getRmnBrandUrl();
        if (!baseUrl) {
            throw new Error("Cannot approve video: missing project or video information");
        }

        return axiosInstant.patch(`${baseUrl}/approve`, {});
    }, [onApproveFn, videoId, getRmnBrandUrl]);

    const value = useMemo(
        () => ({
            isDirty,
            onCreateComment,
            onUpdateComment,
            onDeleteComment,
            getComments,
            onRequestEdit,
            onApprove,
            comments: allComments,
            isCommentLoading,
            setCommentLoading: setIsCommentLoading,
            guestName,
            onGuestNameChange,
            isPublicPage,
            isReviewLocked,
            onCopyComment,
            commentToApply,
            setCommentToApply,
            onCloseReview,
            readOnly,
            project,
            video,
            revisions,
            disableCommentMessage,
            showFeedbackCriteria,
            downloadFileTypes,
            onEditAsComplete,
            onFeedbackSubmit,
            onApproved
        }),
        [
            isDirty,
            onCreateComment,
            onUpdateComment,
            onDeleteComment,
            getComments,
            onRequestEdit,
            onApprove,
            allComments,
            isCommentLoading,
            guestName,
            onGuestNameChange,
            isPublicPage,
            isReviewLocked,
            onCopyComment,
            commentToApply,
            onCloseReview,
            readOnly,
            project,
            video,
            revisions,
            disableCommentMessage,
            showFeedbackCriteria,
            downloadFileTypes,
            onEditAsComplete,
            onFeedbackSubmit,
            onApproved
        ]
    );

    return <VideoFeedbackContext.Provider value={value}>{children}</VideoFeedbackContext.Provider>;
};

export const useVideoFeedback = () => useContext(VideoFeedbackContext);
